import React, { FC, useContext, useState } from "react";
import { IPreferences, KeyAuditCriteria, KeyAuditType } from "../../logic/api";
import { GridLegacy as Grid, Typography, Box } from "@mui/material";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import styles from "./styles";
import { SessionContext } from "../../utility/useSession";
import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import { useTranslation } from "react-i18next";
import { IDropDownListItem } from "@/components/editable/EditableField/fields/DropDownField";


export interface IKeyAuditSettingsV2Props {
  onSave: (newPrefs: Partial<IPreferences>) => Promise<boolean>;
}

export const KeyAuditSettingsV2: FC<IKeyAuditSettingsV2Props> = ({ onSave }) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);

  const preferences = session.current.site?.preferences;
  const keyAuditCriteria = preferences?.key_audit_criteria;

  const [laneCriteria, setLaneCriteria] = useState<KeyAuditCriteria>(
    keyAuditCriteria?.lane.criteria[0] ?? KeyAuditCriteria.AnyMovement
  );
  const [standaloneCriteria, setStandaloneCriteria] = useState<KeyAuditCriteria>(
    keyAuditCriteria?.standalone.criteria[0] ?? KeyAuditCriteria.AnyMovement
  );

  if (!preferences || !keyAuditCriteria) {
    return null;
  }

  const isDoNotAudit = (criteria: KeyAuditCriteria) => criteria === KeyAuditCriteria.DoNotAudit;

  const getCriteriaOptions = (isLane: boolean): IDropDownListItem[] => {
    const options: IDropDownListItem[] = [
      { name: t("settings.keyAuditsUpdate.criteria.AnyMovement"), value: KeyAuditCriteria.AnyMovement.toString() },
      { name: t("settings.keyAuditsUpdate.criteria.24HoursNoMovement"), value: KeyAuditCriteria["24HoursNoMovement"].toString() },
      { name: t("settings.keyAuditsUpdate.criteria.FullBox"), value: KeyAuditCriteria.FullBox.toString() },
    ];

    if (isLane) {
      options.push({ name: t("settings.keyAuditsUpdate.criteria.LaneAuditComplete"), value: KeyAuditCriteria.LaneAuditComplete.toString() });
    }

    options.push({ name: t("settings.keyAuditsUpdate.criteria.DoNotAudit"), value: KeyAuditCriteria.DoNotAudit.toString() });

    return options;
  };

  const getAuditTypeOptions = (isLane: boolean): IDropDownListItem[] => {
    const options: IDropDownListItem[] = [
      { name: t("settings.keyAuditsUpdate.type.Contents"), value: KeyAuditType.Contents.toString() },
    ];

    if (isLane) {
      options.push({ name: t("settings.keyAuditsUpdate.type.Order"), value: KeyAuditType.Order.toString() });
    }

    return options;
  };

  const getCriteriaLabel = (criteria: KeyAuditCriteria) => t(`settings.keyAuditsUpdate.criteria.${KeyAuditCriteria[criteria]}`);
  const getAuditTypeLabel = (auditType: KeyAuditType) => t(`settings.keyAuditsUpdate.type.${KeyAuditType[auditType]}`);

  return (
    <Grid item xs={12}>
      <NotchedCard label={t("settings.keyAuditsUpdate.title")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="key-audit-settings"
          testId="key-audit-settings"
          editType={EditableGroupType.switchableEditing}
          data={preferences}
          onDataChanged={(data) => onSave({ key_audit_criteria: data.key_audit_criteria })}
        >
          <Box data-testid="key-audit-settings-content">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("settings.keyAuditsUpdate.description")}
            </Typography>

            <p style={{ marginBottom: 0 }}>
              <strong>{t("settings.keyAuditsUpdate.lane")}</strong>
            </p>
            <Grid container>
              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAuditsUpdate.criteria.label")}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria ? getCriteriaLabel(data.key_audit_criteria.lane.criteria[0]) : "",
                    value: data.key_audit_criteria?.lane.criteria[0].toString() ?? "",
                  })}
                  onRequestData={async () => getCriteriaOptions(true)}
                  onRequestText={(data) => (data.key_audit_criteria ? getCriteriaLabel(data.key_audit_criteria.lane.criteria[0]) : "")}
                  onChange={(data, value) => {
                    if (data.key_audit_criteria && value.value) {
                      const newCriteria = Number(value.value) as KeyAuditCriteria;
                      data.key_audit_criteria.lane.criteria = [newCriteria];
                      setLaneCriteria(newCriteria);
                    }
                  }}
                  testId="key-audit-settings-lane-criteria"
                />
              </Grid>

              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAuditsUpdate.type.label")}
                  readOnly={isDoNotAudit(laneCriteria)}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria && !isDoNotAudit(laneCriteria)
                      ? getAuditTypeLabel(data.key_audit_criteria.lane.auditType)
                      : "-",
                    value: data.key_audit_criteria?.lane.auditType.toString() ?? "",
                  })}
                  onRequestData={async () => getAuditTypeOptions(true)}
                  onRequestText={(data) =>
                    data.key_audit_criteria && !isDoNotAudit(laneCriteria)
                      ? getAuditTypeLabel(data.key_audit_criteria.lane.auditType)
                      : "-"
                  }
                  onChange={(data, value) => {
                    if (data.key_audit_criteria && value.value) {
                      data.key_audit_criteria.lane.auditType = Number(value.value) as KeyAuditType;
                    }
                  }}
                  testId="key-audit-settings-lane-audit-type"
                />
              </Grid>
            </Grid>

            <p style={{ marginTop: 40, marginBottom: 0 }}>
              <strong>{t("settings.keyAuditsUpdate.standalone")}</strong>
            </p>
            <Grid container>
              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAuditsUpdate.criteria.label")}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria ? getCriteriaLabel(data.key_audit_criteria.standalone.criteria[0]) : "",
                    value: data.key_audit_criteria?.standalone.criteria[0].toString() ?? "",
                  })}
                  onRequestData={async () => getCriteriaOptions(false)}
                  onRequestText={(data) => (data.key_audit_criteria ? getCriteriaLabel(data.key_audit_criteria.standalone.criteria[0]) : "")}
                  onChange={(data, value) => {
                    if (data.key_audit_criteria && value.value) {
                      const newCriteria = Number(value.value) as KeyAuditCriteria;
                      data.key_audit_criteria.standalone.criteria = [newCriteria];
                      setStandaloneCriteria(newCriteria);
                    }
                  }}
                  testId="key-audit-settings-standalone-criteria"
                />
              </Grid>

              <Grid item sm={6} xs={6}>
                <EditableField<IPreferences, IDropDownListItem>
                  useFullWidthWhenViewing
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("settings.keyAuditsUpdate.type.label")}
                  readOnly={isDoNotAudit(standaloneCriteria)}
                  onProvide={(data) => ({
                    name: data.key_audit_criteria && !isDoNotAudit(standaloneCriteria)
                      ? getAuditTypeLabel(data.key_audit_criteria.standalone.auditType)
                      : "-",
                    value: data.key_audit_criteria?.standalone.auditType.toString() ?? "",
                  })}
                  onRequestData={async () => getAuditTypeOptions(false)}
                  onRequestText={(data) =>
                    data.key_audit_criteria && !isDoNotAudit(standaloneCriteria)
                      ? getAuditTypeLabel(data.key_audit_criteria.standalone.auditType)
                      : "-"
                  }
                  onChange={(data, value) => {
                    if (data.key_audit_criteria && value.value) {
                      data.key_audit_criteria.standalone.auditType = Number(value.value) as KeyAuditType;
                    }
                  }}
                  testId="key-audit-settings-standalone-audit-type"
                />
              </Grid>
            </Grid>
          </Box>
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
